import { z } from "zod";
import { createEndpoint } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import { ProjectAttachmentResponseSchema } from "../schemas/projects-v2";

export const DirectChatroomResponseSchema = z.object({
  thread_id: z.string().min(1),
  created: z.boolean(),
  client_org_id: z.number().int().positive(),
  expert_org_id: z.number().int().positive(),
});

export const DirectChatPeerSchema = z.object({
  thread_id: z.string().min(1),
  org_id: z.number().int().positive(),
  name: z.string().min(1),
  avatar_url: z.string().nullable(),
  subtitle: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  profile_url: z.string().url().nullable().optional(),
});
export const DirectChatUserSchema = z.object({
  user_id: z.number().int().positive(),
  name: z.string().min(1),
  avatar_url: z.string().nullable(),
});
export const DirectChatPeersResponseSchema = z.object({
  peers: z.array(DirectChatPeerSchema),
  users: z.array(DirectChatUserSchema).optional(),
});
export const DirectChatSearchResultSchema = DirectChatPeerSchema.extend({ thread_id: z.string().min(1).nullable() });
export const DirectChatSearchResponseSchema = z.object({
  results: z.array(DirectChatSearchResultSchema),
  next_cursor: z.string().nullable(),
});
export type DirectChatSearchResult = z.infer<typeof DirectChatSearchResultSchema>;
export type DirectChatPeersResponse = z.infer<typeof DirectChatPeersResponseSchema>;

export function createDirectChatroomsApi(http: HttpClient, baseUrl: string) {
  const base = `${baseUrl}/direct-chatrooms`;
  const attachments = z.array(ProjectAttachmentResponseSchema);
  const upload = createEndpoint<z.infer<typeof attachments>, FormData, QueryParams, { threadId: string }>(
    http, "POST", "/:threadId/attachments", { base, asFormData: true, responseSchema: attachments, params: ["threadId"] },
  );
  return {
    participantsDisplay: createEndpoint<z.infer<typeof DirectChatPeersResponseSchema>, undefined, { thread_ids: string[] }>(
      http, "GET", "/participants/display", { base, responseSchema: DirectChatPeersResponseSchema },
    ),
    search: createEndpoint<z.infer<typeof DirectChatSearchResponseSchema>, undefined, { q: string; cursor?: string }>(
      http, "GET", "/search", { base, responseSchema: DirectChatSearchResponseSchema },
    ),
    open: createEndpoint<z.infer<typeof DirectChatroomResponseSchema>, { recipient_org_id: number } | { recipient_expert_slug: string }>(
      http, "POST", "", { base, responseSchema: DirectChatroomResponseSchema },
    ),
    uploadAttachments({ threadId, files }: { threadId: string; files: File[] }) {
      const body = new FormData();
      files.forEach((file, index) => body.append(`files[${index}]`, file));
      return upload({ params: { threadId }, body });
    },
  };
}
